/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Layout from '../../components/layout/Layout';
import TextField from '../../components/TextField';
import CustomButton from '../../components/CustomButton';
import { grantCapability } from '../../redux/actions/capabilityGrant';

const AVAILABLE_CAPABILITIES = [
  'baby_first_aid',
  'prenatal_exercises',
  'name_finder',
  'guided_meditation',
  'newborn_care',
];

const CAPABILITY_TRANSLATIONS = {
  baby_first_aid: 'Babaelsősegély tanfolyam',
  prenatal_exercises: 'Szülésre készítő torna',
  name_finder: 'Utónévkereső',
  guided_meditation: 'Relaxációs hanganyagok',
  newborn_care: 'Babagondozás tanfolyam',
};

const CapabilityGrant = () => {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({
    email: '',
    capabilities: [],
    months: 1,
    txid: '',
  });

  const { loading, error, lastGrantData } = useSelector((state) => state.capabilityGrant);

  const handleFieldUpdate = (field, value) => {
    setFormData((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  const handleCapabilityToggle = (capability) => {
    setFormData((prevState) => {
      const isSelected = prevState.capabilities.includes(capability);
      const newCapabilities = isSelected
        ? prevState.capabilities.filter((c) => c !== capability)
        : [...prevState.capabilities, capability];
      return {
        ...prevState,
        capabilities: newCapabilities,
      };
    });
  };

  const handleSubmit = async () => {
    if (!formData.email || !formData.email.trim()) {
      alert('Email is required');
      return;
    }

    if (!formData.capabilities || formData.capabilities.length === 0) {
      alert('Please select at least one capability');
      return;
    }

    const payload = {
      email: formData.email.trim(),
      capabilities: formData.capabilities.join(','),
    };

    if (formData.months && formData.months !== 1) {
      payload.months = parseInt(formData.months, 10);
    }

    if (formData.txid && formData.txid.trim()) {
      payload.txid = formData.txid.trim();
    }

    try {
      await dispatch(grantCapability(payload));
      alert('Premium function granted successfully!');
      // Reset form after successful submission
      setFormData({
        email: '',
        capabilities: [],
        months: 1,
        txid: '',
      });
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Unknown error occurred';
      alert(`Error: ${errorMessage}`);
    }
  };

  const renderResponseData = () => {
    if (!lastGrantData) return null;

    return (
      <div className="mx-12 mt-8 p-4 bg-green-50 border border-green-200 rounded">
        <h3 className="text-lg font-bold mb-4 text-green-800">Last Grant Response</h3>
        <div className="space-y-2">
          <div>
            <span className="font-semibold">User ID:</span> {lastGrantData.userId}
          </div>
          <div>
            <span className="font-semibold">Email:</span> {lastGrantData.email}
          </div>
          <div>
            <span className="font-semibold">Capabilities:</span>{' '}
            {Array.isArray(lastGrantData.capabilities)
              ? lastGrantData.capabilities.join(', ')
              : lastGrantData.capabilities}
          </div>
          <div>
            <span className="font-semibold">Expires At:</span>{' '}
            {new Date(lastGrantData.expiresAt).toLocaleString()}
          </div>
          <div>
            <span className="font-semibold">Transaction ID:</span> {lastGrantData.transactionId}
          </div>
          {lastGrantData.importId && (
            <div>
              <span className="font-semibold">Import ID:</span> {lastGrantData.importId}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <Layout active="capability-grant">
      <div className="flex flex-col items-center justify-center mx-20">
        <div className="w-full flex flex-row items-center justify-center my-4">
          <h1 className="text-2xl font-bold">Grant Premium function to User</h1>
        </div>

        <div className="w-full max-w-3xl">
          <div className="mx-12">
            <TextField
              type="email"
              label="Email *"
              name="email"
              placeholder="user@example.com"
              value={formData.email}
              onChange={(event) => {
                handleFieldUpdate('email', event.target.value);
              }}
              required
            />

            <div className="mb-6">
              <label className="block text-sm font-semibold mb-3">Premium functions *</label>
              <div className="space-y-2 ml-2">
                {AVAILABLE_CAPABILITIES.map((capability) => (
                  <label key={capability} className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.capabilities.includes(capability)}
                      onChange={() => handleCapabilityToggle(capability)}
                      className="w-4 h-4 text-orange border-gray-300 rounded focus:ring-orange cursor-pointer"
                    />
                    <span className="text-sm">
                      {capability.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())} (
                      {CAPABILITY_TRANSLATIONS[capability]})
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <TextField
              type="number"
              label="Months"
              name="months"
              min="1"
              value={formData.months}
              onChange={(event) => {
                handleFieldUpdate('months', event.target.value);
              }}
            />

            <div className="mt-6 flex justify-end">
              <CustomButton
                type="submit"
                className="w-1/3"
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading ? 'Granting...' : 'Grant Premium function'}
              </CustomButton>
            </div>

            {error && (
              <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded text-red-800">
                <strong>Error:</strong> {typeof error === 'string' ? error : JSON.stringify(error)}
              </div>
            )}
          </div>

          {renderResponseData()}
        </div>
      </div>
    </Layout>
  );
};

export default CapabilityGrant;
